import React, { useState, useEffect } from 'react';
import axios from 'axios'; // Added for the backend bridge

// We now accept the 'user' prop from App.js to track who locks the ledger!
const Payroll = ({ user = { username: 'System Admin' } }) => {
    // 1. GLOBAL NAVIGATION & UI STATE
    const [activeTab, setActiveTab] = useState('engine');
    const [printMode, setPrintMode] = useState('sheet');
    const [isSaved, setIsSaved] = useState(false);

    // 2. MODAL & FORM STATE FOR NEW FACULTY
    const [showAddModal, setShowAddModal] = useState(false);
    const [newFaculty, setNewFaculty] = useState({ name: '', position: '', rate: '' });

    // 3. PAYROLL CYCLE & MAKER-CHECKER SETTINGS
    const getCurrentPayrollPeriod = () => {
        const today = new Date();
        const month = today.toLocaleString('default', { month: 'long' });
        const year = today.getFullYear();
        const day = today.getDate();
        if (day <= 15) {
            return `${month} 1-15, ${year}`;
        } else {
            const lastDay = new Date(year, today.getMonth() + 1, 0).getDate();
            return `${month} 16-${lastDay}, ${year}`;
        }
    };
    const [periodStr, setPeriodStr] = useState(getCurrentPayrollPeriod());
    const [standardDays, setStandardDays] = useState(11);
    const [isLocked, setIsLocked] = useState(false);
    const [auditTrail, setAuditTrail] = useState(null); // Tracks WHO locked it and WHEN

    // UI Mockup states for loading drafts
    const [savedDrafts, setSavedDrafts] = useState(['May 1-11, 2026', 'April 16-30, 2026 (Locked)']);
    const [selectedDraft, setSelectedDraft] = useState('');

    // 4. THE LIVE DATABASE STATE 
    const [staffList, setStaffList] = useState([
        { id: 1, name: 'Armelia, Raquel Jr. C.', rate: 485.00, position: 'Teacher 1' },
        { id: 2, name: 'Balansag, Lellan Joy T.', rate: 500.00, position: 'Teacher I/ Student Activities Coordinator', sss: 225 },
        { id: 3, name: 'Benlot, Liezl M.', rate: 525.00, position: 'Librarian - Designate', sss: 225, philhealth: 250, pagibig: 200 },
        { id: 4, name: 'Benlot, Servillano', rate: 300.00, position: 'Garden Manager' },
        { id: 5, name: 'Carson, Via B.', rate: 500.00, position: 'Teacher 1', sss: 225 },
        { id: 6, name: 'Calumbay, Jayson W.', rate: 485.00, position: 'Teacher 1' },
        { id: 7, name: 'Devibar, Kristanya T.', rate: 600.00, position: 'Finance/Accounting Officer', sss: 1350, philhealth: 250, pagibig: 200 },
        { id: 8, name: 'Donsig, Divina T.', rate: 682.00, position: 'School Registrar/ Guidance Councilor-Designate', sss: 1350, philhealth: 250, pagibig: 200 },
        { id: 9, name: 'Donsig, Mary Grace T.', rate: 300.00, position: 'Clinic-in-charge', sss: 225, philhealth: 250, pagibig: 740 },
        { id: 10, name: 'Eslao, Rizza C.', rate: 3000 / 11, position: 'Utility', sss: 225, philhealth: 250, },
        { id: 11, name: 'Eslao, Shiera Mae', rate: 500.00, position: 'Administrative Staff/ICT', sss: 225, pagibig: 200 },
        { id: 12, name: 'Estillore, Cindy R.', rate: 500.00, position: 'Teacher I', sss: 225 },
        { id: 13, name: 'Hinampas, Zyrele Angelie G.', rate: 500.00, position: 'Teacher I', sss: 225 },
        { id: 14, name: 'Jaspe, Alyssa Mae M.', rate: 485.00, position: 'Teacher I' },
        { id: 15, name: 'Kenis, Aleakim B.', rate: 485.00, position: 'Teacher I' },
        { id: 16, name: 'Ondong, Princess Joy T.', rate: 500.00, position: 'Teacher I/PLC Coordinator/Academic Coordinator', sss: 225 },
        { id: 17, name: 'Raganut, Richelle T.', rate: 300.00, position: 'Canteen-in-charge/Cashier', sss: 225, philhealth: 250, pagibig: 200 },
        { id: 18, name: 'Tamayo, Jiean-Mae Y.', rate: 485.00, position: 'Teacher I' },
        { id: 19, name: 'Tan, Alyssa L.', rate: 682.00, position: 'School Principal', sss: 1350, philhealth: 250, pagibig: 200 },
        { id: 20, name: 'Tan, Ignatius L.', rate: 500.00, position: 'Maintenance Manager', sss: 1350, philhealth: 250, pagibig: 200 },
        { id: 21, name: 'Tan, Kim Andrian L.', rate: 500.00, position: 'Prefect of Discipline/Physical Plant Supervisor', sss: 1350, philhealth: 250, pagibig: 200 },
        { id: 22, name: 'Tan, Glicerio J.', rate: 500.00, position: 'School President' },
        { id: 23, name: 'Yam-oc, Krisa Jean B.', rate: 500.00, position: 'Teacher I', sss: 225 },
        { id: 24, name: 'Calipay, Dayana P.', rate: 485.00, position: 'Guidance Councilor-Designate' }

    ]);

    // 5. LIVE HISTORY STATE 
    const [selectedHistoryEmployee, setSelectedHistoryEmployee] = useState('');
    const [historyData, setHistoryData] = useState([]);

    // Auto-fetch the employee's history whenever the dropdown changes
    useEffect(() => {
        if (!selectedHistoryEmployee) {
            setHistoryData([]);
            return;
        }

        const fetchEmployeeHistory = async () => {
            try {
                // Notice the relative route!
                const response = await axios.get(`/api/payroll/history/${encodeURIComponent(selectedHistoryEmployee)}`);
                setHistoryData(response.data);
            } catch (error) {
                console.error("Error fetching employee history:", error);
                alert("Failed to load history from the database.");
            }
        };

        fetchEmployeeHistory();
    }, [selectedHistoryEmployee]);

    // The Master Ledger State
    const [ledger, setLedger] = useState([]);

    // --- LIVE BACKEND: Fetch Drafts for the Dropdown ---
    const fetchDrafts = async () => {
        try {
            const response = await axios.get('/api/payroll/drafts');
            setSavedDrafts(response.data); // Overwrites the dummy data with real DB data!
        } catch (error) {
            console.error("Error fetching drafts from server:", error);
        }
    };

    // Run this once when the Payroll page first loads
    useEffect(() => {
        fetchDrafts();
    }, []);
    // ---------------------------------------------------

    // Auto-sync the ledger whenever staffList or standardDays changes
    useEffect(() => {
        const freshLedger = staffList.map(staff => ({
            daysWorked: standardDays,
            incentives: 0, increase: 0, absentDays: 0, cashAdvance: 0, loan: 0,
            ...staff,
            // Bi-monthly split: halve standard monthly deductions for this period
            sss: staff.sss ? staff.sss / 2 : 0,
            philhealth: staff.philhealth ? staff.philhealth / 2 : 0,
            pagibig: staff.pagibig ? staff.pagibig / 2 : 0
        }));
        setLedger(freshLedger);
        setIsLocked(false);
        setAuditTrail(null);
    }, [staffList, standardDays]);

    // CORE FUNCTIONS
    const calculateRow = (row) => {
        const grossBase = row.daysWorked * row.rate;
        const totalGross = grossBase + Number(row.incentives) + Number(row.increase);
        const absentAmount = Number(row.absentDays) * row.rate;
        const totalDeductions = absentAmount + Number(row.cashAdvance) + Number(row.loan) + Number(row.sss) + Number(row.philhealth) + Number(row.pagibig);
        const netPay = totalGross - totalDeductions;
        return { grossBase, totalGross, absentAmount, totalDeductions, netPay };
    };

    // HARD LIMIT CHECKER: Are there any negative net pays on the board?
    const hasNegativeErrors = ledger.some(row => calculateRow(row).netPay < 0);

    const handleInputChange = (id, field, value) => {
        if (isLocked) return;
        const numValue = value === '' ? 0 : parseFloat(value);
        setLedger(prevLedger => prevLedger.map(row => row.id === id ? { ...row, [field]: numValue } : row));
        setIsSaved(false);
    };

    const handleDeleteFaculty = (id, name) => {
        if (isLocked) return alert("Cannot delete faculty. This payroll period is locked.");
        if (window.confirm(`Are you sure you want to remove ${name} from the active payroll?`)) {
            setStaffList(staffList.filter(staff => staff.id !== id));
        }
    };

    const handleAddFaculty = () => {
        if (!newFaculty.name || !newFaculty.rate) return alert("Please provide a name and rate.");
        const newId = staffList.length > 0 ? Math.max(...staffList.map(s => s.id)) + 1 : 1;
        setStaffList([...staffList, { id: newId, name: newFaculty.name, position: newFaculty.position, rate: parseFloat(newFaculty.rate) }]);
        setNewFaculty({ name: '', position: '', rate: '' });
        setShowAddModal(false);
    };

    // --- LIVE BACKEND: Load a specific draft from the DB ---
    const handleLoadDraft = async () => {
        if (!selectedDraft) return;

        // Strip the "(Locked)" text if it exists so we can search the DB properly
        const cleanPeriod = selectedDraft.replace(' (Locked)', '');
        setPeriodStr(cleanPeriod);

        try {
            // Fetch the exact ledger data from the new Node route
            const response = await axios.get(`/api/payroll/draft/${encodeURIComponent(cleanPeriod)}`);
            const dbData = response.data;

            // Instantly fill the table with the real database numbers!
            setLedger(dbData.ledger_data);

            // If the database says it's locked, lock the UI and show the audit stamp
            if (dbData.is_locked) {
                setIsLocked(true);
                setAuditTrail({
                    name: dbData.locked_by,
                    timestamp: dbData.locked_at
                });
                alert(`Loaded archived period: ${cleanPeriod}. The ledger is locked and cannot be edited.`);
            } else {
                setIsLocked(false);
                setAuditTrail(null);
                alert(`Draft loaded successfully. Ready for audit.`);
            }
        } catch (error) {
            console.error("Error fetching draft data:", error);
            alert("Failed to load draft data from the server. Check the F12 console.");
        }
    };

    // LIVE BACKEND: Save Draft
    const handleSave = async () => {
        if (isLocked || hasNegativeErrors) return;
        try {
            await axios.post('/api/payroll/save-draft', {
                period: periodStr,
                ledger: ledger
            });

            console.log("Ledger Draft successfully saved to PostgreSQL!");

            // REFRESH THE DROPDOWN LIST!
            fetchDrafts();

            setIsSaved(true);
            setTimeout(() => setIsSaved(false), 3000);
        } catch (error) {
            console.error("Backend Error:", error);
            alert("Failed to save draft. Check the F12 Console for details.");
        }
    };

    // LIVE BACKEND: Approve & Lock
    const handleApproveAndLock = async () => {
        if (hasNegativeErrors) return alert("Error: You cannot lock a payroll with negative Net Pay balances.");

        if (window.confirm(`⚠️ WARNING: Are you sure you want to Approve & Lock the payroll for ${periodStr}? Once locked, no further edits can be made.`)) {
            try {
                const currentStamp = new Date().toLocaleString();
                const approverName = user.name || user.username || 'Finance Officer';

                await axios.post('/api/payroll/lock', {
                    period: periodStr,
                    ledger: ledger,
                    lockedBy: approverName,
                    timestamp: currentStamp
                });

                // REFRESH THE DROPDOWN LIST!
                fetchDrafts();

                setIsLocked(true);
                setAuditTrail({ name: approverName, timestamp: currentStamp });
                alert("🔒 Payroll successfully locked and finalized for disbursement.");
            } catch (error) {
                console.error("Backend Error:", error);
                alert("Failed to lock payroll on the server. Check the F12 Console.");
            }
        }
    };


    const triggerPrint = (mode) => {
        setPrintMode(mode);
        setTimeout(() => window.print(), 100);
    };

    // Calculate footer totals
    const totals = ledger.reduce((acc, row) => {
        const calc = calculateRow(row);
        return {
            incentives: acc.incentives + Number(row.incentives), totalGross: acc.totalGross + calc.totalGross,
            absentAmount: acc.absentAmount + calc.absentAmount, cashAdvance: acc.cashAdvance + Number(row.cashAdvance),
            loan: acc.loan + Number(row.loan), sss_ph_pag: acc.sss_ph_pag + Number(row.sss) + Number(row.philhealth) + Number(row.pagibig),
            totalDeductions: acc.totalDeductions + calc.totalDeductions, netPay: acc.netPay + calc.netPay,
        };
    }, { incentives: 0, totalGross: 0, absentAmount: 0, cashAdvance: 0, loan: 0, sss_ph_pag: 0, totalDeductions: 0, netPay: 0 });

    return (
        <div style={{ backgroundColor: '#f8fafc', padding: '20px', minHeight: '100vh', fontFamily: 'Arial, sans-serif' }}>

            {/* DYNAMIC PRINT STYLES */}
            <style>
                {`
                    @media print {
                        @page { size: ${printMode === 'sheet' || printMode === 'history' ? 'A4 landscape' : 'A4 portrait'}; margin: 0.5in; }
                        .no-print { display: none !important; }
                        .print-only { display: block !important; }
                        .payroll-table-container { display: ${printMode === 'slips' ? 'none !important' : 'block'} }
                        .payroll-slips-container { display: ${printMode === 'sheet' || printMode === 'history' ? 'none !important' : 'flex !important'}; visibility: visible !important; }
                        .no-print-border { border: none !important; background: transparent !important; }
                    }
                `}
            </style>

            <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>

                {/* --- HEADER & NAVIGATION TABS --- */}
                <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '2px solid #e2e8f0', paddingBottom: '15px', marginBottom: '25px' }}>
                    <div>
                        <h2 style={{ color: '#0ea5e9', margin: '0 0 10px 0', fontSize: '28px' }}>💰 SHAIS Financial Portal</h2>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => setActiveTab('engine')} style={{ padding: '8px 16px', backgroundColor: activeTab === 'engine' ? '#0ea5e9' : '#e2e8f0', color: activeTab === 'engine' ? 'white' : '#475569', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Active Payroll Engine</button>
                            <button onClick={() => setActiveTab('history')} style={{ padding: '8px 16px', backgroundColor: activeTab === 'history' ? '#8b5cf6' : '#e2e8f0', color: activeTab === 'history' ? 'white' : '#475569', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Salary History Archive</button>
                        </div>
                    </div>
                </div>

                {/* ==========================================
                    TAB 1: ACTIVE PAYROLL ENGINE 
                    ========================================== */}
                {activeTab === 'engine' && (
                    <div className="payroll-table-container">

                        {/* THE MAKER-CHECKER WORKFLOW PANEL */}
                        <div className="no-print" style={{ backgroundColor: '#f1f5f9', padding: '15px', borderRadius: '10px', marginBottom: '20px', border: '1px solid #cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <label style={{ fontWeight: 'bold', color: '#475569' }}>📂 Load Saved Draft:</label>
                                <select value={selectedDraft} onChange={(e) => setSelectedDraft(e.target.value)} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', minWidth: '200px' }}>
                                    <option value="">-- Select Period --</option>
                                    {savedDrafts.map((draft, i) => <option key={i} value={draft}>{draft}</option>)}
                                </select>
                                <button onClick={handleLoadDraft} style={{ padding: '8px 15px', backgroundColor: '#334155', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Load</button>
                            </div>

                            {/* Visual Indicator of Lock Status */}
                            {isLocked && (
                                <div style={{ backgroundColor: '#fee2e2', color: '#b91c1c', padding: '8px 15px', borderRadius: '6px', fontWeight: '900', border: '1px solid #fca5a5' }}>
                                    🔒 PERIOD LOCKED (READ-ONLY)
                                </div>
                            )}
                        </div>

                        {/* HARD LIMIT ERROR BANNER */}
                        {hasNegativeErrors && !isLocked && (
                            <div className="no-print" style={{ backgroundColor: '#fee2e2', color: '#b91c1c', padding: '15px', borderRadius: '10px', marginBottom: '20px', border: '2px solid #ef4444', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                🚨 ERROR: One or more employees have a negative Net Pay. You must adjust deductions before you can save or lock this payroll.
                            </div>
                        )}

                        {/* Control Panel */}
                        <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', gap: '15px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569' }}>Payroll Period</label>
                                    <input type="text" value={periodStr} onChange={(e) => setPeriodStr(e.target.value)} disabled={isLocked} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: 'bold', backgroundColor: isLocked ? '#e2e8f0' : 'white' }} />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569' }}>Standard Days</label>
                                    <input type="number" value={standardDays} onChange={(e) => setStandardDays(Number(e.target.value))} disabled={isLocked} style={{ padding: '8px', width: '80px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: 'bold', backgroundColor: isLocked ? '#e2e8f0' : 'white' }} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                                {!isLocked && (
                                    <>
                                        <button onClick={() => setShowAddModal(true)} style={{ padding: '10px 15px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>➕ Add Faculty</button>
                                        <button onClick={handleSave} disabled={hasNegativeErrors} style={{ padding: '10px 15px', backgroundColor: hasNegativeErrors ? '#94a3b8' : '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: hasNegativeErrors ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>{isSaved ? '✅ Saved!' : '💾 Save Draft'}</button>
                                        <button onClick={handleApproveAndLock} disabled={hasNegativeErrors} style={{ padding: '10px 15px', backgroundColor: hasNegativeErrors ? '#94a3b8' : '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: hasNegativeErrors ? 'not-allowed' : 'pointer', fontWeight: 'bold', boxShadow: hasNegativeErrors ? 'none' : '0 2px 4px rgba(231, 76, 60, 0.3)' }}>🔒 Approve & Lock</button>
                                    </>
                                )}
                                <button onClick={() => triggerPrint('sheet')} style={{ padding: '10px 15px', backgroundColor: '#334155', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>🖨️ Print Sheet</button>
                                <button onClick={() => triggerPrint('slips')} style={{ padding: '10px 15px', backgroundColor: '#f97316', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>🎫 Print Slips</button>
                            </div>
                        </div>

                        {/* Add Faculty Modal */}
                        {showAddModal && !isLocked && (
                            <div className="no-print" style={{ backgroundColor: '#f1f5f9', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #cbd5e1', display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input type="text" placeholder="Full Name" value={newFaculty.name} onChange={e => setNewFaculty({ ...newFaculty, name: e.target.value })} style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                <input type="text" placeholder="Position" value={newFaculty.position} onChange={e => setNewFaculty({ ...newFaculty, position: e.target.value })} style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                <input type="number" placeholder="Daily Rate (₱)" value={newFaculty.rate} onChange={e => setNewFaculty({ ...newFaculty, rate: e.target.value })} style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '120px' }} />
                                <button onClick={handleAddFaculty} style={{ padding: '8px 15px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>Save</button>
                                <button onClick={() => setShowAddModal(false)} style={{ padding: '8px 15px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>Cancel</button>
                            </div>
                        )}

                        {/* Official Letterhead for Print */}
                        <div className="print-only" style={{ display: 'none', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', borderBottom: '2px solid black', paddingBottom: '10px', marginBottom: '15px' }}>
                                <img src="/owl.png" alt="SHAIS Logo" style={{ width: '60px' }} />
                                <div style={{ textAlign: 'center', color: 'black' }}>
                                    <h2 style={{ margin: '0 0 5px 0', fontSize: '20px', textTransform: 'uppercase' }}>San Herminigildo Agro-Industrial School</h2>
                                    <p style={{ margin: '0', fontSize: '12px' }}>P4 Alanib, Lantapan, 8722 Bukidnon</p>
                                    <h3 style={{ margin: '10px 0 0 0', fontSize: '16px' }}>MASTER PAYROLL SHEET</h3>
                                </div>
                            </div>
                            <h3 style={{ textAlign: 'center', margin: '15px 0', fontSize: '14px', textDecoration: 'underline', color: 'black' }}>
                                SALARIES AND WAGES OF TEACHERS FOR THE PERIOD: {periodStr}
                            </h3>
                        </div>

                        {/* Interactive Data Grid */}
                        <div style={{ overflowX: 'auto', border: '1px solid #cbd5e1', borderRadius: '8px', opacity: isLocked ? 0.9 : 1 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '12px', minWidth: '1200px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                                        <th className="no-print" style={{ padding: '10px', textAlign: 'center', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #cbd5e1' }}>Act</th>
                                        <th style={{ padding: '10px', textAlign: 'left', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #cbd5e1' }} rowSpan="2">Name</th>
                                        <th style={{ padding: '10px', textAlign: 'center', borderRight: '1px solid #cbd5e1', borderBottom: '1px solid #cbd5e1', backgroundColor: '#f8fafc' }} colSpan="3">Gross Calculation</th>
                                        <th style={{ padding: '10px', textAlign: 'center', borderRight: '1px solid #cbd5e1', borderBottom: '1px solid #cbd5e1', backgroundColor: '#fee2e2' }} colSpan="6">Deductions</th>
                                        <th style={{ padding: '10px', textAlign: 'center', backgroundColor: '#dcfce7', borderBottom: '1px solid #cbd5e1' }} rowSpan="2">Net Pay</th>
                                    </tr>
                                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1', textAlign: 'center' }}>
                                        <th className="no-print" style={{ padding: '8px', borderRight: '1px solid #e2e8f0' }}></th>
                                        <th style={{ padding: '8px', borderRight: '1px solid #e2e8f0' }}>Rate/Day</th>
                                        <th style={{ padding: '8px', borderRight: '1px solid #e2e8f0' }}>Incentive</th>
                                        <th style={{ padding: '8px', borderRight: '1px solid #cbd5e1' }}>Total Gross</th>
                                        <th style={{ padding: '8px', borderRight: '1px solid #e2e8f0' }}>Abs. Days</th>
                                        <th style={{ padding: '8px', borderRight: '1px solid #e2e8f0' }}>Abs. Amt</th>
                                        <th style={{ padding: '8px', borderRight: '1px solid #e2e8f0' }}>Cash Adv.</th>
                                        <th style={{ padding: '8px', borderRight: '1px solid #e2e8f0' }}>Loan</th>
                                        <th style={{ padding: '8px', borderRight: '1px solid #e2e8f0' }}>SSS/PH/PAG</th>
                                        <th style={{ padding: '8px', borderRight: '1px solid #e2e8f0' }}>Total Ded.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ledger.map((row, index) => {
                                        const calc = calculateRow(row);
                                        const isNegative = calc.netPay < 0;

                                        return (
                                            <tr key={row.id} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: isNegative ? '#fee2e2' : (isLocked ? '#fafafa' : 'white') }}>
                                                <td className="no-print" style={{ padding: '8px', textAlign: 'center', borderRight: '1px solid #e2e8f0' }}>
                                                    <button onClick={() => handleDeleteFaculty(row.id, row.name)} disabled={isLocked} style={{ background: 'none', border: 'none', cursor: isLocked ? 'not-allowed' : 'pointer', color: isLocked ? '#cbd5e1' : '#ef4444' }}>🗑️</button>
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'left', fontWeight: 'bold', borderRight: '1px solid #e2e8f0', color: isLocked ? '#64748b' : 'inherit' }}>{index + 1}. {row.name}</td>
                                                <td style={{ padding: '8px', borderRight: '1px solid #e2e8f0', color: '#64748b' }}>{row.rate.toFixed(2)}</td>
                                                <td style={{ padding: '8px', borderRight: '1px solid #e2e8f0' }}>
                                                    <input type="number" disabled={isLocked} value={row.incentives === 0 ? '' : row.incentives} onChange={(e) => handleInputChange(row.id, 'incentives', e.target.value)} style={{ width: '60px', textAlign: 'right', border: '1px solid #cbd5e1', borderRadius: '4px', backgroundColor: isLocked || isNegative ? 'transparent' : 'white' }} className="no-print-border" />
                                                </td>
                                                <td style={{ padding: '8px', borderRight: '1px solid #cbd5e1', fontWeight: 'bold', backgroundColor: isNegative ? 'transparent' : '#f1f5f9' }}>{calc.totalGross.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                                <td style={{ padding: '8px', borderRight: '1px solid #e2e8f0', backgroundColor: isNegative ? 'transparent' : '#fef2f2' }}>
                                                    <input type="number" step="0.5" disabled={isLocked} value={row.absentDays === 0 ? '' : row.absentDays} onChange={(e) => handleInputChange(row.id, 'absentDays', e.target.value)} style={{ width: '40px', textAlign: 'center', border: '1px solid #fca5a5', borderRadius: '4px', backgroundColor: isLocked || isNegative ? 'transparent' : '#fff5f5' }} className="no-print-border" />
                                                </td>
                                                <td style={{ padding: '8px', borderRight: '1px solid #e2e8f0', color: '#ef4444' }}>{calc.absentAmount > 0 ? calc.absentAmount.toFixed(2) : '-'}</td>
                                                <td style={{ padding: '8px', borderRight: '1px solid #e2e8f0' }}>
                                                    <input type="number" disabled={isLocked} value={row.cashAdvance === 0 ? '' : row.cashAdvance} onChange={(e) => handleInputChange(row.id, 'cashAdvance', e.target.value)} style={{ width: '60px', textAlign: 'right', border: '1px solid #cbd5e1', borderRadius: '4px', backgroundColor: isLocked || isNegative ? 'transparent' : 'white' }} className="no-print-border" />
                                                </td>
                                                <td style={{ padding: '8px', borderRight: '1px solid #e2e8f0' }}>
                                                    <input type="number" disabled={isLocked} value={row.loan === 0 ? '' : row.loan} onChange={(e) => handleInputChange(row.id, 'loan', e.target.value)} style={{ width: '60px', textAlign: 'right', border: '1px solid #cbd5e1', borderRadius: '4px', backgroundColor: isLocked || isNegative ? 'transparent' : 'white' }} className="no-print-border" />
                                                </td>
                                                <td style={{ padding: '8px', borderRight: '1px solid #e2e8f0', display: 'flex', gap: '2px' }}>
                                                    <input type="number" placeholder="SSS" disabled={isLocked} value={row.sss === 0 ? '' : row.sss} onChange={(e) => handleInputChange(row.id, 'sss', e.target.value)} style={{ width: '45px', fontSize: '10px', textAlign: 'right', border: '1px solid #cbd5e1', backgroundColor: isLocked || isNegative ? 'transparent' : 'white' }} className="no-print-border" />
                                                    <input type="number" placeholder="PH" disabled={isLocked} value={row.philhealth === 0 ? '' : row.philhealth} onChange={(e) => handleInputChange(row.id, 'philhealth', e.target.value)} style={{ width: '45px', fontSize: '10px', textAlign: 'right', border: '1px solid #cbd5e1', backgroundColor: isLocked || isNegative ? 'transparent' : 'white' }} className="no-print-border" />
                                                    <input type="number" placeholder="PAG" disabled={isLocked} value={row.pagibig === 0 ? '' : row.pagibig} onChange={(e) => handleInputChange(row.id, 'pagibig', e.target.value)} style={{ width: '45px', fontSize: '10px', textAlign: 'right', border: '1px solid #cbd5e1', backgroundColor: isLocked || isNegative ? 'transparent' : 'white' }} className="no-print-border" />
                                                </td>
                                                <td style={{ padding: '8px', borderRight: '1px solid #cbd5e1', fontWeight: 'bold', color: '#dc2626' }}>{calc.totalDeductions > 0 ? calc.totalDeductions.toLocaleString('en-US', { minimumFractionDigits: 2 }) : '-'}</td>
                                                <td style={{ padding: '8px', fontWeight: '900', color: isNegative ? '#dc2626' : '#166534', backgroundColor: isNegative ? '#fca5a5' : '#f0fdf4', fontSize: '14px' }}>{calc.netPay.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>

                                <tfoot>
                                    <tr style={{ backgroundColor: '#e2e8f0', color: '#0f172a', fontWeight: '900', borderTop: '3px solid #94a3b8' }}>
                                        <td className="no-print" style={{ borderRight: '1px solid #cbd5e1' }}></td>
                                        <td style={{ padding: '12px', textAlign: 'left', borderRight: '1px solid #cbd5e1', textTransform: 'uppercase', fontSize: '14px' }}>Grand Totals</td>
                                        <td style={{ padding: '12px', borderRight: '1px solid #cbd5e1' }}>-</td>
                                        <td style={{ padding: '12px', borderRight: '1px solid #cbd5e1' }}>{totals.incentives.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                        <td style={{ padding: '12px', borderRight: '1px solid #cbd5e1', color: '#0369a1' }}>{totals.totalGross.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                        <td style={{ padding: '12px', borderRight: '1px solid #cbd5e1' }}>-</td>
                                        <td style={{ padding: '12px', borderRight: '1px solid #cbd5e1', color: '#b91c1c' }}>{totals.absentAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                        <td style={{ padding: '12px', borderRight: '1px solid #cbd5e1', color: '#b91c1c' }}>{totals.cashAdvance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                        <td style={{ padding: '12px', borderRight: '1px solid #cbd5e1', color: '#b91c1c' }}>{totals.loan.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                        <td style={{ padding: '12px', borderRight: '1px solid #cbd5e1', color: '#b91c1c' }}>{totals.sss_ph_pag.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                        <td style={{ padding: '12px', borderRight: '1px solid #cbd5e1', color: '#b91c1c' }}>{totals.totalDeductions.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                        <td style={{ padding: '12px', color: '#15803d', fontSize: '16px' }}>{totals.netPay.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {/* THE IMMUTABLE AUDIT TRAIL PRINT STAMP */}
                        {auditTrail && (
                            <div className="print-only" style={{ display: 'none', marginTop: '30px', textAlign: 'left', fontSize: '11px', color: '#0f172a', borderTop: '2px solid black', paddingTop: '10px' }}>
                                <strong>🔒 OFFICIAL AUDIT TRAIL:</strong> This payroll period was verified, approved, and permanently locked by <strong>{auditTrail.name}</strong> on <strong>{auditTrail.timestamp}</strong>. No further modifications are permitted.
                            </div>
                        )}
                    </div>
                )}

                {/* ==========================================
                    TAB 2: SALARY HISTORY ARCHIVE
                    ========================================== */}
                {activeTab === 'history' && (
                    <div className="payroll-table-container">
                        <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f3f4f6', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                            <div>
                                <h3 style={{ margin: '0 0 10px 0', color: '#1e293b' }}>Search Employee Records</h3>
                                <select value={selectedHistoryEmployee} onChange={(e) => setSelectedHistoryEmployee(e.target.value)} style={{ padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', width: '300px', fontSize: '14px' }}>
                                    <option value="">-- Select Faculty / Staff --</option>
                                    {staffList.map(staff => (
                                        <option key={staff.id} value={staff.name}>{staff.name}</option>
                                    ))}
                                </select>
                            </div>
                            {selectedHistoryEmployee && (
                                <button onClick={() => triggerPrint('history')} style={{ padding: '10px 20px', backgroundColor: '#334155', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>🖨️ Print Record</button>
                            )}
                        </div>

                        {selectedHistoryEmployee ? (
                            <div>
                                <div className="print-only" style={{ display: 'none', marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', borderBottom: '2px solid black', paddingBottom: '10px', marginBottom: '15px' }}>
                                        <img src="/owl.png" alt="SHAIS Logo" style={{ width: '60px' }} />
                                        <div style={{ textAlign: 'center', color: 'black' }}>
                                            <h2 style={{ margin: '0 0 5px 0', fontSize: '20px', textTransform: 'uppercase' }}>San Herminigildo Agro-Industrial School</h2>
                                            <p style={{ margin: '0', fontSize: '12px' }}>P4 Alanib, Lantapan, 8722 Bukidnon</p>
                                            <h3 style={{ margin: '10px 0 0 0', fontSize: '16px' }}>EMPLOYEE SALARY HISTORY REPORT</h3>
                                        </div>
                                    </div>
                                </div>
                                <h2 style={{ color: '#0f172a', borderBottom: '2px solid #e2e8f0', paddingBottom: '10px' }}>Ledger for: <span style={{ color: '#8b5cf6' }}>{selectedHistoryEmployee}</span></h2>
                                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', marginTop: '15px' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#e2e8f0' }}>
                                            <th style={{ padding: '12px', borderBottom: '2px solid #cbd5e1' }}>Payroll Period</th>
                                            <th style={{ padding: '12px', borderBottom: '2px solid #cbd5e1' }}>Total Gross</th>
                                            <th style={{ padding: '12px', borderBottom: '2px solid #cbd5e1' }}>Total Deductions</th>
                                            <th style={{ padding: '12px', borderBottom: '2px solid #cbd5e1' }}>Net Pay Received</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyData.length > 0 ? (
                                            historyData.map((record, i) => (
                                                <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                    <td style={{ padding: '12px', fontWeight: 'bold' }}>{record.period}</td>
                                                    <td style={{ padding: '12px', color: '#16a34a' }}>₱ {record.gross.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                                    <td style={{ padding: '12px', color: '#dc2626' }}>₱ {record.deductions.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                                    <td style={{ padding: '12px', fontWeight: 'bold', fontSize: '16px' }}>₱ {record.net.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                                                    No finalized payroll records found for this employee.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="no-print" style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>
                                <h3>Select an employee from the dropdown above to view their salary history.</h3>
                            </div>
                        )}
                    </div>
                )}

                {/* ==========================================
                INDIVIDUAL PAYSLIP GENERATOR (PRINT ONLY)
                ========================================== */}
                <div className="payroll-slips-container print-slips-container" style={{ display: 'none' }}>
                    {ledger.map((row, index) => {
                        const calc = calculateRow(row);
                        return (
                            <div key={row.id} className="payslip-ticket">
                                {/* Header */}
                                <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                        <img src="/owl.png" alt="Logo" style={{ width: '30px' }} />
                                        <div>
                                            <h4 style={{ margin: 0, fontSize: '11px', color: 'black' }}>San Herminigildo Agro-Industrial School</h4>
                                            <p style={{ margin: 0, fontSize: '9px', color: 'black' }}>P-4 Alanib, Lantapan, Bukidnon</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Meta Data */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid black', paddingBottom: '5px', marginBottom: '10px', fontSize: '11px' }}>
                                    <span><strong>NO.</strong> {index + 1}</span>
                                    <span><strong>Date:</strong> {periodStr}</span>
                                </div>

                                {/* Employee Info */}
                                <div style={{ marginBottom: '10px' }}>
                                    <p style={{ margin: '0 0 5px 0', fontSize: '11px' }}>Employee Name:</p>
                                    <p style={{ margin: 0, fontWeight: 'bold', fontSize: '13px', textAlign: 'center', textTransform: 'uppercase', borderBottom: '1px solid black', paddingBottom: '5px' }}>{row.name}</p>
                                </div>

                                {/* Orange Banner */}
                                <div style={{ backgroundColor: '#f97316', color: 'black', textAlign: 'center', fontWeight: 'bold', fontSize: '12px', padding: '4px', border: '1px solid black', marginBottom: '10px' }}>
                                    PAY SLIP
                                </div>

                                {/* Financials */}
                                <div style={{ fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                                        <span>Basic Salary:</span>
                                        <span style={{ borderBottom: '1px solid black', width: '50%', textAlign: 'right' }}>₱ {calc.totalGross.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                    </div>

                                    <p style={{ margin: '5px 0 0 0', fontWeight: 'bold' }}>Less Deduction:</p>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Cash Advance:</span>
                                        <span style={{ borderBottom: '1px solid black', width: '50%', textAlign: 'right' }}>{row.cashAdvance > 0 ? `₱ ${Number(row.cashAdvance).toFixed(2)}` : '₱'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Loan:</span>
                                        <span style={{ borderBottom: '1px solid black', width: '50%', textAlign: 'right' }}>{row.loan > 0 ? `₱ ${Number(row.loan).toFixed(2)}` : '₱'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>SSS:</span>
                                        <span style={{ borderBottom: '1px solid black', width: '50%', textAlign: 'right' }}>{row.sss > 0 ? `₱ ${Number(row.sss).toFixed(2)}` : '₱'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Philhealth:</span>
                                        <span style={{ borderBottom: '1px solid black', width: '50%', textAlign: 'right' }}>{row.philhealth > 0 ? `₱ ${Number(row.philhealth).toFixed(2)}` : '₱'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Pag-ibig:</span>
                                        <span style={{ borderBottom: '1px solid black', width: '50%', textAlign: 'right' }}>{row.pagibig > 0 ? `₱ ${Number(row.pagibig).toFixed(2)}` : '₱'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Absent:</span>
                                        <span style={{ borderBottom: '1px solid black', width: '50%', textAlign: 'right' }}>{calc.absentAmount > 0 ? `₱ ${calc.absentAmount.toFixed(2)}` : ''}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                                        <span>Total Deduction:</span>
                                        <span style={{ borderBottom: '1px solid black', width: '50%', textAlign: 'right' }}>{calc.totalDeductions > 0 ? `₱ ${calc.totalDeductions.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '₱'}</span>
                                    </div>
                                </div>

                                {/* Net Pay & Signature */}
                                <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', fontSize: '14px', fontWeight: 'bold' }}>
                                    <span>Net Pay:</span>
                                    <span style={{ borderBottom: '2px solid black', width: '50%', textAlign: 'right', fontSize: '16px' }}>₱ {calc.netPay.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                </div>

                                <div style={{ marginTop: '40px', textAlign: 'center' }}>
                                    <div style={{ borderTop: '1px solid black', width: '60%', margin: '0 auto', paddingTop: '5px', fontSize: '11px', color: 'black' }}>
                                        Cashier / Disbursed By
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

            </div>
        </div>
    );
};

export default Payroll;